import { useEffect, useRef, useState } from 'react';

export const DEFAULT_STATS = {
  monthlyMinutes: 522253,
  realtimeEvents: 935812506,
  realtimeUsers: 63,
  totalNumberDownloaded: 3851441
};

export const DEFAULT_STATS_ENDPOINT =
  'https://tbymwdm93a.execute-api.eu-west-3.amazonaws.com/ga-report';

function toNonNegativeInt(value, fallback) {
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric)) return fallback;
  const intValue = Math.trunc(numeric);
  if (intValue < 0) return fallback;
  return intValue;
}

export function normalizeStatsPayload(payload, fallback = DEFAULT_STATS) {
  const source = payload && typeof payload === 'object' ? payload : {};

  return {
    monthlyMinutes: toNonNegativeInt(source.monthlyMinutes, fallback.monthlyMinutes),
    realtimeEvents: toNonNegativeInt(source.realtimeEvents, fallback.realtimeEvents),
    realtimeUsers: toNonNegativeInt(source.realtimeUsers, fallback.realtimeUsers),
    totalNumberDownloaded: toNonNegativeInt(
      source.totalNumberDownloaded,
      fallback.totalNumberDownloaded
    )
  };
}

export function mergeStats(prev, next) {
  // Prevent counters that should be monotonically increasing from jumping backwards.
  return {
    ...prev,
    monthlyMinutes: Math.max(prev.monthlyMinutes, next.monthlyMinutes),
    realtimeEvents: Math.max(prev.realtimeEvents, next.realtimeEvents),
    totalNumberDownloaded: Math.max(prev.totalNumberDownloaded, next.totalNumberDownloaded),
    // Real-time users can go up/down.
    realtimeUsers: next.realtimeUsers
  };
}

export async function fetchStatsFromEndpoint({
  endpointUrl = DEFAULT_STATS_ENDPOINT,
  fetchImpl = fetch,
  signal
} = {}) {
  const response = await fetchImpl(endpointUrl, {
    method: 'GET',
    headers: {
      accept: '*/*',
      'accept-language': 'vi,en-US;q=0.9,en;q=0.8,es;q=0.7',
      origin: 'https://www.web3intelligence.com',
      referer: 'https://www.web3intelligence.com/'
    },
    signal
  });

  if (!response.ok) return null;
  const data = await response.json();
  return normalizeStatsPayload(data);
}

export function startRealtimeEventsTicker({
  setStats,
  intervalMs = 1000,
  minIncrement = 5,
  maxIncrement = 50,
  rng = Math.random
}) {
  const safeMin = Math.max(0, Math.trunc(minIncrement));
  const safeMax = Math.max(safeMin, Math.trunc(maxIncrement));

  const id = setInterval(() => {
    const increment = Math.floor(rng() * (safeMax - safeMin + 1)) + safeMin;
    setStats((prev) => ({ ...prev, realtimeEvents: prev.realtimeEvents + increment }));
  }, intervalMs);

  return () => clearInterval(id);
}

export function useLiveStats({
  endpointUrl = DEFAULT_STATS_ENDPOINT,
  pollMs = 30000,
  tickMs = 1000,
  minEventIncrement = 5,
  maxEventIncrement = 50,
  initialStats = DEFAULT_STATS,
  fetchImpl = fetch
} = {}) {
  const [stats, setStats] = useState(initialStats);
  const inFlightRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const poll = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        const remote = await fetchStatsFromEndpoint({
          endpointUrl,
          fetchImpl,
          signal: controller.signal
        });

        if (!cancelled && remote) {
          setStats((prev) => mergeStats(prev, remote));
        }
      } catch {
        // Keep previous/default values if API fails.
      } finally {
        inFlightRef.current = false;
      }
    };

    poll();
    const id = setInterval(poll, pollMs);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(id);
    };
  }, [endpointUrl, pollMs, fetchImpl]);

  useEffect(() => {
    return startRealtimeEventsTicker({
      setStats,
      intervalMs: tickMs,
      minIncrement: minEventIncrement,
      maxIncrement: maxEventIncrement
    });
  }, [tickMs, minEventIncrement, maxEventIncrement]);

  return { stats, setStats };
}

